import React from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { NeoBadge, NeoButton, NeoModal } from '../../../../components/Common';

/**
 * Renderiza um valor de qualquer tipo de forma legível.
 */
const renderValue = (val) => {
    if (val === null || val === undefined) return <span className="text-muted fst-italic">Vazio</span>;
    if (typeof val === 'boolean') return val ? <NeoBadge color="success">Sim</NeoBadge> : <NeoBadge color="danger">Não</NeoBadge>;
    if (Array.isArray(val)) {
        if (val.length === 0) return <span className="text-muted fst-italic">Lista vazia</span>;
        return (
            <ul className="mb-0 ps-3 font-size-12">
                {val.map((item, i) => (
                    <li key={i}>{typeof item === 'object' ? <pre className="mb-0 bg-light p-1 rounded font-size-11">{JSON.stringify(item, null, 2)}</pre> : String(item)}</li>
                ))}
            </ul>
        );
    }
    if (typeof val === 'object') {
        // Timestamp do Firebase
        if (val.seconds) {
            return format(new Date(val.seconds * 1000), 'dd/MM/yyyy HH:mm', { locale: ptBR });
        }
        return <pre className="mb-0 bg-light p-1 rounded font-size-11">{JSON.stringify(val, null, 2)}</pre>;
    }
    return String(val);
};

/**
 * Detecta se o objeto de details está em formato "diff" (cada campo tem { from, to }).
 */
function isDiffFormat(details) {
    if (!details || typeof details !== 'object') return false;
    const entries = Object.entries(details);
    if (entries.length === 0) return false;
    const diffCount = entries.filter(([, val]) =>
        val && typeof val === 'object' && !Array.isArray(val) && ('from' in val || 'to' in val)
    ).length;
    return diffCount > entries.length / 2;
}

/**
 * Detecta se é um processing trace (pipeline de venda).
 */
function isProcessingTrace(details) {
    return details && typeof details === 'object' && ('steps' in details || 'financialRecords' in details);
}

/**
 * Detecta se é um log de venda do frontend (SalesService.processSale).
 */
function isFrontendSaleLog(details) {
    return details && typeof details === 'object' && ('totalAmount' in details || 'items' in details) && ('member' in details || 'paymentMethods' in details);
}

/**
 * Renderiza o Processing Trace do pipeline de vendas.
 */
const ProcessingTraceView = ({ trace }) => {
    return (
        <div>
            {/* Cabeçalho */}
            <div className="d-flex flex-wrap gap-3 mb-3 p-2 bg-light rounded">
                <div><small className="text-muted d-block">Venda</small><strong>#{trace.saleNumber}</strong></div>
                <div><small className="text-muted d-block">Membro</small><strong>{trace.memberName}</strong></div>
                <div><small className="text-muted d-block">Valor</small><strong>R$ {(trace.totalAmount || 0).toFixed(2)}</strong></div>
                <div><small className="text-muted d-block">Data</small><strong>{renderValue(trace.saleDate)}</strong></div>
            </div>

            {/* Steps */}
            {trace.steps?.length > 0 && (
                <div className="mb-3">
                    <h6 className="text-muted mb-2"><i className="mdi mdi-format-list-checks me-1" />Pipeline de Execução</h6>
                    <ol className="mb-0 font-size-13">
                        {trace.steps.map((step, i) => (
                            <li key={i} className={step.includes('❌') ? 'text-danger fw-medium' : step.includes('⚠️') ? 'text-warning' : ''}>
                                {step}
                            </li>
                        ))}
                    </ol>
                </div>
            )}

            {/* Financial Records */}
            {trace.financialRecords?.length > 0 && (
                <div className="mb-3">
                    <h6 className="text-muted mb-2"><i className="mdi mdi-cash-multiple me-1" />Registros Financeiros ({trace.financialRecords.length})</h6>
                    <div className="neo-table-wrapper border rounded-3 overflow-hidden">
                        <table className="neo-table font-size-12 mb-0">
                            <thead>
                                <tr>
                                    <th>Método</th>
                                    <th>Valor</th>
                                    <th>Vencimento</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {trace.financialRecords.map((rec, i) => (
                                    <tr key={i}>
                                        <td>{rec.method}</td>
                                        <td className="fw-medium">R$ {(rec.gross || 0).toFixed(2)}</td>
                                        <td>{rec.due}</td>
                                        <td>
                                            <NeoBadge color={rec.status === 'PAID' ? 'success' : 'warning'}>
                                                {rec.status === 'PAID' ? 'Pago' : 'Pendente'}
                                            </NeoBadge>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Member Activation */}
            {trace.memberActivation && (
                <div className="mb-3 p-2 border rounded bg-success bg-opacity-10">
                    <h6 className="text-success mb-1"><i className="mdi mdi-account-check me-1" />Ativação do Membro</h6>
                    <small>Status: <NeoBadge color="secondary">{trace.memberActivation.previousStatus || "N/A"}</NeoBadge> → <NeoBadge color="success">active</NeoBadge></small>
                </div>
            )}

            {/* Trial Conversion */}
            {trace.trialConversion && (
                <div className="mb-3 p-2 border rounded bg-info bg-opacity-10">
                    <h6 className="text-info mb-1"><i className="mdi mdi-swap-horizontal me-1" />Conversão de Experimental</h6>
                    <small>Matrícula: {trace.trialConversion.enrollmentId}</small>
                </div>
            )}

            {/* Metrics Update */}
            {trace.metricsUpdate && (
                <div className="mb-3 p-2 border rounded bg-primary bg-opacity-10">
                    <h6 className="text-primary mb-1"><i className="mdi mdi-chart-line me-1" />Métricas Atualizadas</h6>
                    <small>
                        Bruto: R$ {(trace.metricsUpdate.gross || 0).toFixed(2)} |
                        Desconto: R$ {(trace.metricsUpdate.discount || 0).toFixed(2)} |
                        Líquido: R$ {(trace.metricsUpdate.net || 0).toFixed(2)} |
                        Data: {renderValue(trace.metricsUpdate.date)}
                    </small>
                </div>
            )}

            {/* Errors */}
            {trace.errors?.length > 0 && (
                <div className="p-2 border border-danger rounded bg-danger bg-opacity-10">
                    <h6 className="text-danger mb-2"><i className="mdi mdi-alert-circle me-1" />Erros ({trace.errors.length})</h6>
                    {trace.errors.map((err, i) => (
                        <div key={i} className="text-danger font-size-12 mb-1">{err}</div>
                    ))}
                </div>
            )}
        </div>
    );
};

/**
 * Renderiza log de venda do frontend (dados completos da venda).
 */
const FrontendSaleView = ({ details }) => {
    return (
        <div>
            {/* Resumo Financeiro */}
            <div className="d-flex flex-wrap gap-3 mb-3 p-2 bg-light rounded">
                <div><small className="text-muted d-block">Total</small><strong className="text-success">R$ {(details.totalAmount || 0).toFixed(2)}</strong></div>
                <div><small className="text-muted d-block">Subtotal</small><strong>R$ {(details.subtotal || 0).toFixed(2)}</strong></div>
                {(details.discountTotal > 0) && <div><small className="text-muted d-block">Desconto</small><strong className="text-danger">-R$ {(details.discountTotal || 0).toFixed(2)}</strong></div>}
                {(details.balance > 0) && <div><small className="text-muted d-block">Saldo</small><strong className="text-warning">R$ {(details.balance || 0).toFixed(2)}</strong></div>}
                <div><small className="text-muted d-block">Data</small><strong>{renderValue(details.date || 'N/A')}</strong></div>
            </div>

            {/* Membro e Consultor */}
            <div className="d-flex flex-wrap gap-4 mb-3">
                {details.member && (
                    <div className="p-2 border rounded flex-fill">
                        <h6 className="text-muted mb-1"><i className="mdi mdi-account me-1" />Membro</h6>
                        <div className="font-size-13"><strong>{details.member.name}</strong> <small className="text-muted">(#{details.member.number})</small></div>
                    </div>
                )}
                {details.consultant && (
                    <div className="p-2 border rounded flex-fill">
                        <h6 className="text-muted mb-1"><i className="mdi mdi-account-tie me-1" />Consultor</h6>
                        <div className="font-size-13"><strong>{details.consultant.name}</strong></div>
                    </div>
                )}
            </div>

            {/* Itens vendidos */}
            {details.items?.length > 0 && (
                <div className="mb-3">
                    <h6 className="text-muted mb-2"><i className="mdi mdi-cart me-1" />Itens Vendidos ({details.items.length})</h6>
                    <div className="neo-table-wrapper border rounded-3 overflow-hidden">
                        <table className="neo-table font-size-12 mb-0">
                            <thead>
                                <tr>
                                    <th>Item</th>
                                    <th>Tipo</th>
                                    <th>Valor</th>
                                    <th>Contrato</th>
                                </tr>
                            </thead>
                            <tbody>
                                {details.items.map((item, i) => (
                                    <tr key={i}>
                                        <td className="fw-medium">{item.name}</td>
                                        <td>
                                            <NeoBadge color={item.type === 'membership' ? 'info' : 'secondary'}>
                                                {item.type}
                                            </NeoBadge>
                                        </td>
                                        <td>R$ {(item.totalPrice || item.unitPrice || 0).toFixed(2)}</td>
                                        <td className="font-size-11">
                                            {item.startDate
                                                ? <span>{renderValue(item.startDate)} — {item.duration} {item.durationType === 'monthly' ? 'mês(es)' : item.durationType === 'yearly' ? 'ano(s)' : item.durationType}</span>
                                                : <span className="text-muted">—</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Formas de Pagamento */}
            {details.paymentMethods?.length > 0 && (
                <div className="mb-3">
                    <h6 className="text-muted mb-2"><i className="mdi mdi-credit-card me-1" />Pagamento</h6>
                    <div className="neo-table-wrapper border rounded-3 overflow-hidden">
                        <table className="neo-table font-size-12 mb-0">
                            <thead>
                                <tr><th>Método</th><th>Valor</th><th>Parcelas</th><th>Bandeira</th></tr>
                            </thead>
                            <tbody>
                                {details.paymentMethods.map((pm, i) => (
                                    <tr key={i}>
                                        <td className="fw-medium">{pm.method}</td>
                                        <td>R$ {(pm.value || 0).toFixed(2)}</td>
                                        <td>{pm.installments}x</td>
                                        <td>{pm.brand || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Processing Trace (cálculo de datas) */}
            {details.processingTrace?.length > 0 && (
                <div className="mb-3">
                    <h6 className="text-muted mb-2"><i className="mdi mdi-math-log me-1" />Cálculo de Datas (Contratos)</h6>
                    <div className="neo-table-wrapper border rounded-3 overflow-hidden">
                        <table className="neo-table font-size-11 mb-0">
                            <thead>
                                <tr><th>Plano</th><th>Início Informado</th><th>Início Calculado</th><th>Fim Calculado</th><th>Duração</th></tr>
                            </thead>
                            <tbody>
                                {details.processingTrace.map((t, i) => (
                                    <tr key={i}>
                                        <td>{t.itemName}</td>
                                        <td>{renderValue(t.inputStartDate)}</td>
                                        <td>{renderValue(t.computedStartDate)}</td>
                                        <td className="fw-medium">{renderValue(t.computedEndDate)}</td>
                                        <td>{t.durationCalculated} {t.durationTypeCalculated}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Contratos criados */}
            {details.createdMemberships && Object.keys(details.createdMemberships).length > 0 && (
                <div className="p-2 border rounded bg-success bg-opacity-10">
                    <h6 className="text-success mb-1"><i className="mdi mdi-check-circle me-1" />Contratos Criados</h6>
                    <small className="font-size-11 text-muted">{Object.entries(details.createdMemberships).map(([k, v]) => `${k}: ${v}`).join(', ')}</small>
                </div>
            )}
        </div>
    );
};

/**
 * Renderiza uma tabela diff (formato { campo: { from, to } }).
 */
const DiffView = ({ details }) => {
    const changes = Object.entries(details);
    return (
        <div className="neo-table-wrapper border rounded-3 overflow-hidden">
            <table className="neo-table align-middle font-size-13 mb-0">
                <thead>
                    <tr>
                        <th>Campo Alterado</th>
                        <th style={{ width: '35%' }}>Valor Anterior</th>
                        <th style={{ width: '35%' }}>Novo Valor</th>
                    </tr>
                </thead>
                <tbody>
                    {changes.map(([field, data], idx) => (
                        <tr key={idx}>
                            <td className="fw-medium text-muted">{field}</td>
                            <td className="text-danger bg-danger bg-opacity-10">
                                {renderValue(data?.from)}
                            </td>
                            <td className="text-success bg-success bg-opacity-10">
                                {renderValue(data?.to)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

/**
 * Renderiza uma tabela simples de dados (formato { campo: valor }).
 */
const DataView = ({ details }) => {
    const entries = Object.entries(details);
    return (
        <div className="neo-table-wrapper border rounded-3 overflow-hidden">
            <table className="neo-table align-middle font-size-13 mb-0">
                <thead>
                    <tr>
                        <th style={{ width: '30%' }}>Campo</th>
                        <th>Valor</th>
                    </tr>
                </thead>
                <tbody>
                    {entries.map(([field, value], idx) => (
                        <tr key={idx}>
                            <td className="fw-medium text-muted">{field}</td>
                            <td>{renderValue(value)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

/**
 * AuditDetailsModal — Modal de detalhes do registro de auditoria.
 * Migrado 100% para o Neo Design System com NeoModal, NeoBadge e NeoButton.
 */
const AuditDetailsModal = ({ isOpen, toggle, log }) => {
    if (!log || !log.details) return null;

    const details = log.details;

    // Detecta o formato dos details e renderiza adequadamente
    const isTrace = isProcessingTrace(details);
    const isSaleLog = !isTrace && isFrontendSaleLog(details);
    const isDiff = !isTrace && !isSaleLog && isDiffFormat(details);

    const modalTitle = isTrace 
        ? 'Pipeline da Venda (Backend)' 
        : isSaleLog 
            ? 'Detalhes da Venda' 
            : isDiff 
                ? 'Detalhes da Alteração' 
                : 'Detalhes';

    const modalSubtitle = `${log.entityName || log.module} — Ação ${log.action} realizada por ${log.userName}`;

    // Ações de rodapé usando NeoButton
    const footerActions = (
        <NeoButton 
            variant="outline-chocolate"
            size="sm"
            onClick={toggle}
            style={{ minWidth: '90px' }}
        >
            Fechar
        </NeoButton>
    );

    return (
        <NeoModal
            isOpen={isOpen}
            toggle={toggle}
            size="lg"
            title={modalTitle}
            subtitle={modalSubtitle}
            footerActions={footerActions}
            scrollable={true}
        >
            {isTrace && <ProcessingTraceView trace={details} />}
            {isSaleLog && <FrontendSaleView details={details} />}
            {isDiff && <DiffView details={details} />}
            {!isTrace && !isSaleLog && !isDiff && <DataView details={details} />}
        </NeoModal>
    );
};

export default AuditDetailsModal;
